import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Spinner } from 'react-bootstrap';
import { FaFileLines } from 'react-icons/fa6';
import { useEscapeKey } from '../../hooks';
import { BACKEND_URL_PREFIX } from '../../utils/const';
import styles from './cardStyles.module.css';

// даты приходят HTTP-датой «Thu, 25 Jun 2026 00:00:00 GMT» — читаем в UTC,
// иначе на отрицательном смещении часового пояса съезжает день
export const formatDate = value => (value ? moment.utc(value).format('DD.MM.YYYY') : '');

const DocumentCard = ({
    row,
    onClose,
    filesList,
    filesIsLoading,
    filesErrorStatus,
    getFilesList,
}) => {

    const [zoom, setZoom] = useState(null);

    // Esc закрывает верхний слой: сначала просмотр скана, потом само окно
    useEscapeKey(() => (zoom ? setZoom(null) : onClose()));

    useEffect(() => {
        if (row) getFilesList(row.mid, row.djid);
    }, [row]);

    // Страница под окном не прокручивается, пока окно открыто. Исчезнувшая полоса
    // прокрутки возмещается отступом справа — иначе фон списка дёргается на её ширину.
    useEffect(() => {
        const { body } = document;
        const previousOverflow = body.style.overflow;
        const previousPadding = body.style.paddingRight;
        const scrollbar = window.innerWidth - document.documentElement.clientWidth;

        body.style.overflow = 'hidden';
        if (scrollbar > 0) {
            const current = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
            body.style.paddingRight = `${current + scrollbar}px`;
        }

        return () => {
            body.style.overflow = previousOverflow;
            body.style.paddingRight = previousPadding;
        };
    }, []);

    if (!row) return null;

    const field = (label, value, className) => (
        <div className={`${styles.fld} ${className || ''}`}>
            <span className={styles.lb}>{label}</span>
            <div className={styles.vl}>
                {value || value === 0
                    ? value
                    : <span className={styles.isEmpty}>не заполнено</span>}
            </div>
        </div>
    );

    const renderFile = (el) => {
        // имя файла с пробелами и кириллицей ломает адрес — в ответе для этого
        // есть готовое поле urlencoded
        const fileLink = `${BACKEND_URL_PREFIX}/edudocs/${row.mid}/${row.djid}/${el.urlencoded || encodeURIComponent(el.file)}`;
        const isImage = (el.type || '').includes('image');

        return (
            <div className={styles.file} key={el.file}>
                {isImage ? (
                    <div className={styles.filePreview} onClick={() => setZoom(fileLink)}>
                        <img src={fileLink} alt={el.file} />
                    </div>
                ) : (
                    <div className={styles.fileIcon}>
                        <FaFileLines size={90} />
                    </div>
                )}
                <div className={styles.fileName}>{el.file}</div>
                <a className={styles.fileLink} href={fileLink} download={el.file}>Скачать</a>
            </div>
        );
    };

    // Год выпуска — это год перевода получателя со статусом «Выпуск», а не год
    // выдачи документа, поэтому вывести его из «Даты выдачи» нельзя. В ответе
    // GET /edudocs такого ключа пока нет (есть только одноимённый параметр отбора),
    // и поле показывает «не заполнено»; ключ читается по обоим вероятным именам,
    // чтобы поле ожило само, как только бэк начнёт его отдавать.
    const graduationYear = row['Год выпуска'] ?? row.year;

    const subtitleParts = [
        row['Вид документа'],
        row['Регистр. номер'] ? `регистрационный номер ${row['Регистр. номер']}` : null,
        row['Дата выдачи'] ? `выдан ${formatDate(row['Дата выдачи'])}` : null,
    ].filter(Boolean);

    return (
        <>
            <div className={styles.ov} onClick={(e) => e.target === e.currentTarget && onClose()}>
                <div className={styles.win}>
                    <div className={styles.winHead}>
                        <div className={styles.winTitle}>{row['ФИО'] || 'Документ об образовании'}</div>
                        <div className={styles.winSub}>{subtitleParts.join(' · ')}</div>
                    </div>

                    <div className={styles.winBody}>
                        <div className={styles.grp}>
                            <div className={styles.grpTitle}>Документ</div>
                            <div className={styles.fform}>
                                {field('Вид документа', row['Вид документа'])}
                                {field('Регистрационный номер', row['Регистр. номер'])}
                                {field('Дата выдачи', formatDate(row['Дата выдачи']))}
                                {field('Серия', row['Серия'])}
                                {field('Номер', row['Номер'])}
                                {field('Год выпуска', graduationYear)}
                            </div>
                        </div>
                        <div className={styles.grp}>
                            <div className={styles.grpTitle}>Получатель</div>
                            <div className={styles.fform}>
                                {field('Категория получателя', row['Категория'])}
                                {field('ФИО получателя', row['ФИО'], styles.span2)}
                                {field('Уровень образования', row['ООП'])}
                                {field('Специальность', row.speciality, styles.span2)}
                                {field('Специализация', row.specialization, styles.span2)}
                                {field('Квалификация', row.classificationname)}
                            </div>
                        </div>

                        <div className={styles.grp}>
                            <div className={styles.grpTitle}>Решение государственной аттестационной комиссии</div>
                            <div className={styles.fform}>
                                {field('Дата принятия решения', formatDate(row['Дата принятия решения ГАК']))}
                                {field('Номер протокола', row['Номер протокола ГАК'])}
                                {field('Председатель комиссии', row['ФИО председателя ГАК'])}
                            </div>
                        </div>

                        <div className={styles.grp}>
                            <div className={styles.grpTitle}>Примечание</div>
                            <div className={styles.fform}>
                                {field('Примечание', row['Примечание'], styles.wide)}
                            </div>
                        </div>

                        <div className={styles.grp}>
                            <div className={styles.grpTitle}>Файлы документа</div>
                            {filesIsLoading ? (
                                <Spinner animation="border" role="status" size="sm">
                                    <span className="sr-only">Loading...</span>
                                </Spinner>
                            ) : filesErrorStatus ? (
                                <div className={styles.filesError}>
                                    {filesErrorStatus === 403
                                        ? 'Доступ к файлам документа запрещён'
                                        : filesErrorStatus === 0
                                            ? 'Нет связи с сервером'
                                            : 'Не удалось загрузить файлы документа'}
                                </div>
                            ) : filesList && filesList.length ? (
                                <div className={styles.files}>{filesList.map(renderFile)}</div>
                            ) : (
                                <div className={styles.filesNote}>Файлы не приложены</div>
                            )}
                        </div>
                    </div>

                    <div className={styles.winFoot}>
                        <button className={styles.closeBtn} onClick={onClose}>Закрыть</button>
                    </div>
                </div>
            </div>

            {zoom && (
                <div className={styles.zoom} onClick={() => setZoom(null)}>
                    <button className={styles.zoomClose} onClick={() => setZoom(null)}>×</button>
                    <img src={zoom} alt="" onClick={(e) => e.stopPropagation()} />
                </div>
            )}
        </>
    );
};

export default DocumentCard;
